// src/stores/CartStore.ts
import { makeAutoObservable, runInAction } from 'mobx';
import type { CartLineItem, MenuItem, Modifier, CheckoutInfo } from '../types';
import API from '../api/client';
import {
  getNextTierDiscountInfo,
  getRestaurantDeliveryDiscount,
  parseAddressComponents,
} from '../utilityFunctions';

/**
 * Example tax rate of 8.75%.
 */
const TAX_RATE = 0.0875;

/**
 * A MobX store for managing the user's cart and related checkout
 * computations (subtotal, discount, etc.).
 */
class CartStore {
  /**
   * Items currently in the cart.
   */
  cartItems: CartLineItem[] = [];

  /**
   * The un-discounted delivery fee (e.g. from Uber Direct).
   * If user chooses Delivery, you'll set this after
   * calling your /api/estimate-delivery.
   */
  deliveryFee: number = 0;

  /**
   * The quote ID returned by Uber Direct's create-quote endpoint.
   */
  deliveryQuoteId: string | null = null;

  /**
   * User tip amount in dollars (if any).
   */
  tip: number = 0;

  /**
   * A code the user entered for a promotion, e.g. 'FIRST5OFF'
   */
  promoCode: string | null = null;

  /**
   * The discount (in dollars) from that promo code, e.g. 5.00
   */
  promoDiscount: number = 0;

  /**
   * The user’s existing loyalty points from your DB,
   * if you want to track them in the cart store.
   */
  existingPoints: number = 0;

  /**
   * The type of delivery your customer will be using to collect their order.
   */
  deliveryType: 'pickup' | 'delivery' = 'delivery';

  /**
   * The discount your restaurant covers for the user’s delivery cost.
   * e.g. if the user’s cost is 9.99 and your discount is 3.00,
   * net is 6.99.
   *
   * If the discount is >= entire cost => free delivery.
   */
  deliveryStreet: string | undefined = '';
  deliveryCity: string | undefined = '';
  deliveryState: string | undefined = '';
  deliveryZip: string | undefined = '';
  deliveryCountry: string | undefined = 'US';
  deliveryLat: number | undefined;
  deliveryLng: number | undefined;
  address: google.maps.GeocoderAddressComponent[] = [];
  distance: number = 0;
  // 1) New field to track whether the sidebar is open
  isSidebarOpen = false;

  constructor() {
    // Pass the second arg (configuration) empty, the third sets autoBind = true
    makeAutoObservable(this, {}, { autoBind: true });
  }

  openSidebar() {
    this.isSidebarOpen = true;
  }

  closeSidebar() {
    this.isSidebarOpen = false;
  }

  // Example helper for toggling
  toggleSidebar() {
    this.isSidebarOpen = !this.isSidebarOpen;
  }

  // --------------------------------------------------------------------------
  // Delivery Estimate
  // --------------------------------------------------------------------------
  /**
   * Calls your /api/estimate-delivery/ endpoint with
   * full address data for Uber Direct (street/city/state/zip/country).
   * On success, sets deliveryFee and deliveryQuoteId.
   */
  async fetchDeliveryEstimate(): Promise<{
    delivery_estimate: number;
    quote_id: string;
  }> {
    const res = await API.post('/api/estimate-delivery/', {
      delivery_street: this.deliveryStreet,
      delivery_city: this.deliveryCity,
      delivery_state: this.deliveryState,
      delivery_zip: this.deliveryZip,
      delivery_country: this.deliveryCountry,
    });

    if (res.data.error) {
      // The API returned an error shape
      throw new Error(res.data.message || 'Delivery estimate error');
    }

    // e.g. { error: false, delivery_estimate: 4.99, quote_id: "abc123" }
    const { delivery_estimate, quote_id } = res.data;
    runInAction(() => {
      this.deliveryFee = delivery_estimate;
      this.deliveryQuoteId = quote_id;
    });
    return { delivery_estimate, quote_id };
  }

  // --------------------------------------------------------------------------
  // Setters
  // --------------------------------------------------------------------------
  setDeliveryFee(fee: number) {
    this.deliveryFee = fee;
  }

  setTip(amount: number) {
    this.tip = amount;
  }

  setPromoCode(code: string) {
    this.promoCode = code;
  }

  setPromoDiscount(amount: number) {
    this.promoDiscount = amount;
  }

  setExistingPoints(points: number) {
    this.existingPoints = points;
  }

  setDeliveryStreet(deliveryStreet: string) {
    this.deliveryStreet = deliveryStreet;
  }
  setDeliveryCity(deliveryCity: string) {
    this.deliveryCity = deliveryCity;
  }
  setDeliveryState(deliveryState: string) {
    this.deliveryState = deliveryState;
  }
  setDeliveryZip(deliveryZip: string) {
    this.deliveryZip = deliveryZip;
  }
  setDeliveryCountry(deliveryCountry: string) {
    this.deliveryCountry = deliveryCountry;
  }
  setDeliveryLng(deliveryLng: number) {
    this.deliveryLng = deliveryLng;
  }
  setDeliveryLat(deliveryLat: number) {
    this.deliveryLat = deliveryLat;
  }
  setDeliveryType(deliveryType: 'pickup' | 'delivery') {
    this.deliveryType = deliveryType;
  }
  setAddress(address: google.maps.GeocoderAddressComponent[]) {
    this.address = address;
  }
  setDistance(distance: number) {
    this.distance = distance;
    this.tip = this.basicTip;
  }
  setGoogleAddressComponents(
    addressComponents: google.maps.GeocoderAddressComponent[],
  ) {
    // This is a MobX action that parses everything synchronously
    const { street, city, state, zip, country } =
      parseAddressComponents(addressComponents);
    this.deliveryStreet = street;
    this.deliveryCity = city;
    this.deliveryState = state;
    this.deliveryZip = zip;
    this.deliveryCountry = country || 'US';
  }

  // --------------------------------------------------------------------------
  // Cart item operations
  // --------------------------------------------------------------------------
  /**
   * Adds a given item with optional modifiers. If the same config
   * already exists, increments quantity. Returns the line-item ID.
   */
  addToCart(
    menuItem: MenuItem,
    quantity: number = 1,
    selectedModifiers: Modifier[] = [],
  ): string {
    const signature = this.buildSignature(menuItem, selectedModifiers);

    // Check if we have the same config
    const existing = this.cartItems.find((li) => li.id === signature);
    if (existing) {
      existing.quantity += quantity;
      return existing.id;
    } else {
      // Create a new line item
      const newLine: CartLineItem = {
        id: signature,
        menuItem,
        quantity,
        selectedModifiers,
      };
      this.cartItems.push(newLine);
      return newLine.id;
    }
  }

  /**
   * Removes a line item from the cart entirely by ID.
   */
  removeLineItem(lineItemId: string) {
    this.cartItems = this.cartItems.filter((li) => li.id !== lineItemId);
  }

  /**
   * Updates the quantity of a line item. If quantity <= 0 => remove it.
   */
  updateQuantity(lineItemId: string, newQty: number) {
    const item = this.cartItems.find((li) => li.id === lineItemId);
    if (!item) return;
    if (newQty <= 0) {
      this.removeLineItem(lineItemId);
    } else {
      item.quantity = newQty;
    }
  }

  /**
   * Clears the cart, and also resets the discount/promo states and tip.
   */
  clearCart() {
    this.cartItems = [];
    this.deliveryFee = 0;
    this.deliveryQuoteId = null;
    this.tip = 0;
    this.promoDiscount = 0;
    // keep existingPoints if you want or reset it too
  }

  // --------------------------------------------------------------------------
  // Computed / derived values
  // --------------------------------------------------------------------------
  get restaurantDiscount(): number {
    if (this.deliveryType === 'delivery') {
      return getRestaurantDeliveryDiscount(this.cartSubtotal);
    }
    return 0;
  }
  get nextTierDiscountInfo() {
    if (this.deliveryType === 'delivery') {
      return getNextTierDiscountInfo(this.cartSubtotal);
    }
    return 0;
  }
  get basicTip(): number {
    return Math.min(Math.max(1, this.distance * 1), this.cartSubtotal * 0.1);
  }
  get plusTip(): number {
    return Math.min(Math.max(2, this.distance * 1.5), this.cartSubtotal * 0.15);
  }

  get superTip(): number {
    return Math.min(Math.max(3, this.distance * 2), this.cartSubtotal * 0.2);
  }

  /**
   * The raw cost of line items (before any promo discount).
   */
  get cartSubtotal(): number {
    return this.cartItems.reduce((sum, li) => sum + this.lineItemTotal(li), 0);
  }

  /**
   * The cost of line items minus any promo discount, never below 0.
   */
  get cartSubtotalAfterPromo(): number {
    return Math.max(0, this.cartSubtotal - this.promoDiscount);
  }

  /**
   * The tax on that discounted subtotal.
   */
  get cartTax(): number {
    return this.cartSubtotalAfterPromo * TAX_RATE;
  }

  /**
   * The net cost of delivery after your restaurant discount is applied.
   */
  get netDeliveryCost(): number {
    return Math.max(0, this.deliveryFee - this.restaurantDiscount);
  }

  /**
   * The total "before tip": (subtotal after promo) + tax + netDeliveryCost.
   */
  get finalBeforeTip(): number {
    return (
      this.cartSubtotalAfterPromo +
      this.cartTax +
      (this.deliveryType === 'delivery' ? this.netDeliveryCost : 0)
    );
  }

  /**
   * The total including the user's chosen tip.
   */
  get finalWithTip(): number {
    return (
      this.finalBeforeTip + (this.deliveryType === 'delivery' ? this.tip : 0)
    );
  }

  /**
   * Number of line items (summing quantities).
   */
  get totalQuantity(): number {
    return this.cartItems.reduce((sum, li) => sum + li.quantity, 0);
  }

  /**
   * Helper for computing a single line item total:
   * base price + sum of modifiers, all * quantity.
   */
  lineItemTotal(li: CartLineItem): number {
    const base = li.menuItem.base_price ?? 0;
    const modTotal = li.selectedModifiers
      ? li.selectedModifiers.reduce((acc, m) => acc + m.price, 0)
      : 0;
    return (base + modTotal) * li.quantity;
  }

  // --------------------------------------------------------------------------
  // Loyalty Points to see current balance
  // --------------------------------------------------------------------------
  /**
   * Calls /api/loyalty/points with phone number.
   * Returns points from the server.
   */

  async fetchLoyaltyPointsByPhone(phone: string) {
    if (!phone) return;
    try {
      const res = await API.get(
        `/api/loyalty/points/?phone=${encodeURIComponent(phone)}`,
      );
      this.setExistingPoints(parseFloat(res.data.points));
    } catch (error) {
      console.error('Failed to fetch loyalty points:', error);
      this.setExistingPoints(0);
    }
  }

  async validatePromoCode(code: string, phone?: string) {
    if (!code) return;
    try {
      const res = await API.post('/api/promo-validate/', { code, phone });
      if (res.data.valid) {
        // Now that we know it's valid, we can apply a "preliminary" discount
        const { discount_type, discount_value } = res.data;
        let discount = 0;

        // For a fully accurate discount, you’d need the user's cart subtotal
        // & replicate how the server calculates discount.
        // For example:
        if (discount_type === 'fixed') {
          // Cap at cartSubtotal:
          discount = Math.min(parseFloat(discount_value), this.cartSubtotal);
        } else if (discount_type === 'percent') {
          discount = this.cartSubtotal * (parseFloat(discount_value) / 100);
        }

        this.setPromoCode(code);
        this.setPromoDiscount(discount);

        return { success: true, discount };
      } else {
        this.setPromoCode('');
        this.setPromoDiscount(0);
        return { success: false, message: res.data.message };
      }
    } catch (err) {
      console.error('Promo validate error:', err);
      this.setPromoCode('');
      this.setPromoDiscount(0);
      return { success: false, message: 'Server error validating promo code' };
    }
  }

  // --------------------------------------------------------------------------
  // Checkout call to create the order on the server
  // --------------------------------------------------------------------------
  /**
   * Calls /api/create-order/ with relevant info, including
   * cart items, user data, and optional tip/delivery fields.
   * Returns { order_id, client_secret, total } from the server.
   */
  async checkoutCartWithInfo(checkoutInfo: CheckoutInfo): Promise<{
    order_id: number;
    client_secret: string;
    total: string;
  }> {
    // 1) Build the cart_items payload
    const cart_items = this.cartItems.map((li) => ({
      menu_item_id: li.menuItem.id,
      quantity: li.quantity,
      modifiers: li.selectedModifiers?.map((m) => m.id),
    }));

    // 2) Build overall payload
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const payload: any = {
      cart_items,
      name: checkoutInfo.name,
      email: checkoutInfo.email,
      phone: checkoutInfo.phone,
      delivery_type: this.deliveryType,
      marketing_opt_in: checkoutInfo.marketingOptIn ?? false,
      promo_code: this.promoCode,
    };

    // If user chose delivery, add address fields, cost, quoteId, etc.
    if (this.deliveryType === 'delivery') {
      if (this.deliveryStreet) payload.delivery_street = this.deliveryStreet;
      if (this.deliveryCity) payload.delivery_city = this.deliveryCity;
      if (this.deliveryState) payload.delivery_state = this.deliveryState;
      if (this.deliveryZip) payload.delivery_zip = this.deliveryZip;
      if (this.deliveryCountry) payload.delivery_country = this.deliveryCountry;
      if (this.deliveryLat !== undefined)
        payload.delivery_lat = this.deliveryLat;
      if (this.deliveryLng !== undefined)
        payload.delivery_lng = this.deliveryLng;
      if (this.deliveryQuoteId)
        payload.delivery_quote_id = this.deliveryQuoteId;
      if (this.deliveryFee !== undefined)
        payload.delivery_cost = this.netDeliveryCost;
      if (this.tip) payload.tip = this.tip;
    }

    // 3) POST to your server’s /api/create-order/ endpoint
    const res = await API.post('/api/create-order/', payload);
    // 4) The server returns e.g.: { order_id, client_secret, total }
    return res.data;
  }

  // --------------------------------------------------------------------------
  // Finalize order after payment, if desired
  // --------------------------------------------------------------------------
  /**
   * Optionally call this to finalize the order after Stripe payment
   * is confirmed on the client. The server will check if it's paid,
   * then dispatch tasks to Clover, Uber, etc.
   */
  async finalizeOrder(orderId: number): Promise<void> {
    const res = await API.post(`/api/finalize-order/${orderId}/`);
    // If server responds with an error shape, you can throw here:
    if (res.data.error) {
      throw new Error(res.data.error);
    }
    // Otherwise, success.
    // e.g. {message: "Order finalization triggered"}
  }

  // --------------------------------------------------------------------------
  // Private helper to build a unique signature for line items
  // --------------------------------------------------------------------------
  private buildSignature(menuItem: MenuItem, modifiers: Modifier[]): string {
    const id = menuItem.id;
    // Sort modifier IDs so order doesn't matter
    const modIds = modifiers.map((m) => m.id).sort((a, b) => a - b);
    return `${id}--${modIds.join(',')}`;
  }
}

const cartStore = new CartStore();
export default cartStore;
